import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock, Mail, User as UserIcon, Phone, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";

const FEE = Number(process.env.REACT_APP_SIGNUP_FEE_INR || 69);

export default function Signup() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password !== f.confirm) { setErr("Passwords don't match"); return; }
    if (f.password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", {
        name: f.name, email: f.email, password: f.password, phone: f.phone || null,
      });
      localStorage.setItem("pinslib_token", data.token);
      setUser(data.user);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(errMsg(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-[#0F172A] grid-bg p-12 flex-col justify-between overflow-hidden">
        <Logo light />
        <div className="relative z-10 fade-up">
          <Badge className="bg-amber-500/90 text-amber-950 hover:bg-amber-500/90 mb-5 font-mono text-[11px] uppercase tracking-widest">One-time ₹{FEE} activation</Badge>
          <h1 className="font-heading text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
            Create your<br />Pinslib account.
          </h1>
          <p className="text-slate-400 mt-6 max-w-md text-base leading-relaxed">
            Mock tests designed by professionals from Aakash Institute and Unacademy — timed, auto-graded and reviewed, so you walk into the real thing already prepared.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {["Sign up", `Pay ₹${FEE}`, "Attempt"].map((t, i) => (
            <div key={t} className="fade-up border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-300" style={{ animationDelay: `${150 + i * 100}ms` }}>
              <span className="font-mono text-blue-400 mr-1.5">0{i + 1}</span>{t}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-10"><Logo /></div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-[#1D4ED8]" />Create account
          </h2>
          <p className="text-slate-500 mt-2 text-sm">
            New here? Sign up and pay a one-time ₹{FEE} activation fee to unlock all mock tests.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full name</Label>
              <div className="relative">
                <UserIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="name" required minLength={2} value={f.name} onChange={set("name")}
                  className="pl-9 h-11 bg-white" placeholder="Your name" data-testid="signup-name-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="email" required value={f.email} onChange={set("email")}
                  className="pl-9 h-11 bg-white" placeholder="you@example.com" data-testid="signup-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone (optional)</Label>
              <div className="relative">
                <Phone className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="phone" type="tel" value={f.phone} onChange={set("phone")}
                  className="pl-9 h-11 bg-white" placeholder="10-digit number" data-testid="signup-phone-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="password" type="password" required minLength={6} value={f.password} onChange={set("password")}
                  className="pl-9 h-11 bg-white" placeholder="At least 6 characters" data-testid="signup-password-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm password</Label>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="confirm" type="password" required value={f.confirm} onChange={set("confirm")}
                  className="pl-9 h-11 bg-white" placeholder="Re-enter password" data-testid="signup-confirm-input" />
              </div>
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="signup-error">{err}</div>}
            <Button type="submit" disabled={loading} data-testid="signup-submit-button"
              className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white text-white font-medium transition-colors">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>
          <p className="text-xs text-slate-500 mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-[#1D4ED8] hover:underline" data-testid="signup-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
